/* global google */

import type { PropsWithChildren } from 'react'
import { Component } from 'react'
import * as React from 'react'
import { InfoWindow as InfoBox } from '@react-google-maps/api'
import styled, { ThemeProvider, withTheme } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import Breakpoint from 'components/Common/Breakpoint'
import Group from 'components/utils/Group'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import OfferPricing from 'components/Common/OfferPricing'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { Provider } from 'react-redux'
import type { AppStore } from 'src/client/store'
import { scrollToId } from 'lib/window/scrollUtils'
import truncateText from 'lib/string/truncateText'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import { FormattedMessage } from 'react-intl'
import { LAST_MINUTE_PRODUCT_CODE, LAST_MINUTE_PRODUCT_NAME_MESSAGE } from 'constants/lastMinute'

/*
  Note: rem() is not used for this component because it is only for desktop (and mobile landscape),
  and positioning the info box when its width can change would be a pain because of the way it is
  positioned using a google maps Size object.
 */

const InfoWindowContentWrapper = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  overflow: hidden;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
  margin-left: auto;
  width: 100%;
  max-width: ${rem(300)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(530)};
    flex-direction: row;
  }
`

const ImageBlock = styled.div`
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(200)};
    margin-right: ${rem(10)};
  }
`

const StyledImage = styled(ResponsiveImage)`
  width: 100%;
  margin-bottom: ${rem(10)};

  ${mediaQueryUp.tablet} {
    width: ${rem(200)};
    margin-bottom: 0;
  }
`

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(300)};
    min-width: ${rem(200)};
  }

  ${mediaQueryDown.tablet} {
    width: 100%;
  }
`

const BottomLine = styled.div`
  margin-top: auto;
`

const CloseButton = styled(Clickable)`
  display: none;
  position: absolute;
  right: 0;
  top: 0;
  padding: 0;
  color: ${props => props.theme.palette.neutral.default.four};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const StyledOfferPricing = styled(OfferPricing)`
  margin-top: ${rem(10)};
  margin-bottom: ${rem(10)};
`

const Title = styled(BodyText)`
  padding-right: ${rem(30)};
`

const Description = styled(BodyText)`
  height: ${rem(60)};
  margin-top: ${rem(10)};
  margin-bottom: ${rem(10)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    height: ${rem(135)};
  }
`

const LastMinuteBadgeWrapper = styled.div`
  position: absolute;
  top: ${rem(4)};
  left: ${rem(4)};
  width: ${rem(80)};
  height: ${rem(80)};

  img {
    max-width: 100%;
    height: auto;
  }
`

interface Props {
  currencyCode: string
  marker: App.MapMarker
  offer?: App.Offer | App.OfferSummary
  onCloseClick: () => void
  onProductClick: (marker: App.MapMarker) => void
  regionCode: string
  showFlights?: boolean
  theme: any
  store: AppStore
}

class InfoWindow extends Component<PropsWithChildren<Props>> {
  render() {
    const {
      marker,
      onCloseClick,
      onProductClick,
      theme,
      store,
      offer,
      showFlights,
    } = this.props
    const overlayImageLabel = offer?.overlayImageLabel
    const isLastMinuteBadge = overlayImageLabel?.alt === LAST_MINUTE_PRODUCT_CODE
    const overlayImageBadge = (() => {
      if (!overlayImageLabel) {
        return null
      }

      if (isLastMinuteBadge) {
        return (
          <FormattedMessage
            {...LAST_MINUTE_PRODUCT_NAME_MESSAGE}
          >
            {(lastMinuteBadgeAlt: React.ReactNode) => (
              <Image
                alt={typeof lastMinuteBadgeAlt === 'string' ? lastMinuteBadgeAlt : LAST_MINUTE_PRODUCT_CODE}
                id={overlayImageLabel.id}
                width={80}
                height={80}
              />
            )}
          </FormattedMessage>
        )
      }

      return <Image alt={overlayImageLabel.alt} id={overlayImageLabel.id} width={80} height={80} />
    })()

    const locationContent = marker.locationHeading && marker.locationSubheading
      ? (
          <FormattedMessage
            id="61woj329unqpAAzp6Kux"
            defaultMessage="{locationHeading} - <sub>{locationSubheading}</sub>"
            description="Location heading and subheading with dash separator in map info window"
            values={{
              locationHeading: <Caption variant="medium" as="span">{marker.locationHeading}</Caption>,
              locationSubheading: marker.locationSubheading,
              sub: (chunks: React.ReactNode) => <Caption variant="medium" colour="neutral-three" as="span">{chunks}</Caption>,
            }}
          />
        )
      : (
          <Caption variant="medium" as="span">
            {marker.locationHeading || marker.locationSubheading}
          </Caption>
        )

    const options = {
      enableEventPropagation: true,
      alignBottom: true,
      closeBoxURL: '',
      maxWidth: 570,
      pixelOffset: new google.maps.Size(0, -55),
    }

    return <InfoBox
      position={{
        lat: marker.latitude,
        lng: marker.longitude,
      }}
      options={options}
      onDomReady={() => {
        const content = document.querySelector('.gm-style-iw')
        if (content) {
          content.removeChild(content.childNodes[1])
        }
      }}
    >
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <InfoWindowContentWrapper>
            <CloseButton onClick={onCloseClick}>
              <LineTimesIcon size={14} />
            </CloseButton>
            <Wrapper>
              {marker.imageId && (
                <ImageBlock>
                  {overlayImageBadge && <LastMinuteBadgeWrapper>{overlayImageBadge}</LastMinuteBadgeWrapper>}
                  <ClickableLink
                    href={marker.link}
                    onClick={() => onProductClick(marker)}
                    target="_blank"
                  >
                    <StyledImage
                      alt={marker.name}
                      aria-hidden="true"
                      gravity="auto"
                      mobileAspectRatio="5:3"
                      tabletAspectRatio="9:10"
                      desktopAspectRatio="9:10"
                      id={marker.imageId}
                    />
                  </ClickableLink>
                </ImageBlock>
              )}
              <ContentBlock>
                <OfferLabels offer={offer} />
                {(marker.locationHeading || marker.locationSubheading) &&
                  <Group direction="horizontal" verticalAlign="center">
                    <div>{locationContent}</div>
                  </Group>}
                <Title variant="large" weight="bold">{marker.name}</Title>
                {offer && <>
                  {/* The 'as' clobbering is wrong here, but this component is so old and probably not really used anymore */}
                  <StyledOfferPricing offer={offer as App.TourOffer} showFlights={showFlights} />
                  <BottomLine>
                    <TextButton
                      kind="primary"
                      target="_blank"
                      onClick={() => onProductClick(marker)}
                      fit="flex"
                      href={marker.link}
                    >
                      <FormattedMessage
                        id="DPrKVCER_UzqVSFeZ8fr"
                        defaultMessage="View details"
                        description="Button text to view offer details from map info window"
                      />
                    </TextButton>
                  </BottomLine>
                </>}
                {!offer && <>
                  <Breakpoint max="mobile">
                    {marker.description && <Description variant="large">{truncateText(marker.description, 120)}</Description>}
                  </Breakpoint>
                  <Breakpoint min="tablet">
                    {marker.description && <Description variant="large">{truncateText(marker.description, 270)}</Description>}
                  </Breakpoint>
                  <BottomLine>
                    <TextButton kind="primary" fit="flex" onClick={() => scrollToId(marker.id)}>
                      <FormattedMessage
                        id="DPrKVCER_UzqVSFeZ8fr"
                        defaultMessage="View details"
                        description="Button text to view offer details from map info window"
                      />
                    </TextButton>
                  </BottomLine>
                </>}
              </ContentBlock>
            </Wrapper>
          </InfoWindowContentWrapper>
        </ThemeProvider>
      </Provider>
    </InfoBox>
  }
}

// Have to cast the type because the types for `withTheme` are broken. See https://github.com/styled-components/styled-components/issues/4082
export default withTheme(InfoWindow) as React.ComponentType<Omit<PropsWithChildren<Props>, 'theme'>>
